import { useMemo, useRef, useState } from 'react';
import { FontIcon, Panel, PanelType, PrimaryButton } from '@fluentui/react';
import {
  Button,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { ArrowDownRegular, ArrowUpRegular } from '@fluentui/react-icons';
import { useBoolean } from '@fluentui/react-hooks';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '@moko/ui';
import { appRoles } from '../../authConfig';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  getTimeServices,
  updateTimeService,
  type ITimeService,
} from '../../services/pricingService';

const columns: DataGridColumn<ITimeService>[] = [
  { key: 'code', name: 'Code', minWidth: 80, width: 140, isSortable: true },
  { key: 'name', name: 'Name', minWidth: 160, width: 280, isSortable: true },
  {
    key: 'sortOrder',
    name: 'Sort order',
    minWidth: 90,
    width: 120,
    isSortable: true,
  },
  {
    key: 'isActive',
    name: 'Active',
    minWidth: 80,
    width: 100,
    value: (item) => (item.isActive ? 'Yes' : 'No'),
  },
];

const TimeServices = () => {
  const navigate = useNavigate();
  const profileData = useAtomValue(profileDataAtom);
  const isAdmin = profileData.roles.includes(appRoles.Admin);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const [selected, setSelected] = useState<ITimeService>();
  const [orderItems, setOrderItems] = useState<ITimeService[]>([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [isReorderOpen, { setTrue: showReorder, setFalse: hideReorder }] =
    useBoolean(false);

  const commands: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'newTimeService',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => {
          void navigate('/newTimeService');
        },
        disabled: !isAdmin,
      },
      {
        key: 'editTimeService',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => {
          if (selected) void navigate(`/editTimeService?id=${selected.id}`);
        },
        disabled: !isAdmin || !selected,
      },
      {
        key: 'reorderTimeServices',
        text: 'Reorder',
        icon: <FontIcon iconName="Sort" />,
        onClick: () => {
          const abortController = new AbortController();
          getTimeServices(abortController, 500, 1)
            .then((page) => {
              setOrderItems(
                [...page.items].sort((a, b) => a.sortOrder - b.sortOrder)
              );
              showReorder();
            })
            .catch((error: { message?: string }) => {
              setErrorMessage(error.message ?? 'Failed to load time services.');
            });
        },
        disabled: !isAdmin,
      },
    ],
    [isAdmin, navigate, selected, setErrorMessage, showReorder]
  );

  return (
    <>
      <PagedDataGrid<ITimeService>
        ref={gridRef}
        columns={columns}
        getAction={getTimeServices}
        commandBarItems={commands}
        selectionMode="single"
        onSelectionChange={setSelected}
      />
      <Panel
        isOpen={isReorderOpen}
        onDismiss={hideReorder}
        type={PanelType.medium}
        headerText="Reorder time services"
        isFooterAtBottom
        onRenderFooterContent={() => (
          <PrimaryButton
            text="Save"
            disabled={orderSaving || orderItems.length === 0}
            onClick={() => {
              const abortController = new AbortController();
              setOrderSaving(true);
              Promise.all(
                orderItems.map((item, index) => {
                  const sortOrder = index + 1;
                  if (item.sortOrder === sortOrder) return Promise.resolve();
                  return updateTimeService(abortController, item.id, {
                    code: item.code,
                    name: item.name,
                    sortOrder,
                    isActive: item.isActive,
                  });
                })
              )
                .then(() => {
                  hideReorder();
                  gridRef.current?.refresh();
                  setSuccessMessage('Time service order saved.');
                })
                .catch((error: { message?: string }) => {
                  setErrorMessage(error.message ?? 'Failed to save order.');
                })
                .finally(() => setOrderSaving(false));
            }}
          />
        )}
      >
        <FluentProvider theme={webLightTheme}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginTop: 16,
            }}
          >
            {orderItems.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 12px',
                  border: '1px solid #e1dfdd',
                  borderRadius: 4,
                }}
              >
                <span>
                  {item.code} {item.name}
                </span>
                <span style={{ display: 'flex', gap: 4 }}>
                  <Button
                    appearance="subtle"
                    icon={<ArrowUpRegular />}
                    disabled={index === 0}
                    aria-label="Move up"
                    onClick={() =>
                      setOrderItems((rows) => {
                        if (index === 0) return rows;
                        const next = [...rows];
                        const [row] = next.splice(index, 1);
                        next.splice(index - 1, 0, row);
                        return next;
                      })
                    }
                  />
                  <Button
                    appearance="subtle"
                    icon={<ArrowDownRegular />}
                    disabled={index === orderItems.length - 1}
                    aria-label="Move down"
                    onClick={() =>
                      setOrderItems((rows) => {
                        if (index === rows.length - 1) return rows;
                        const next = [...rows];
                        const [row] = next.splice(index, 1);
                        next.splice(index + 1, 0, row);
                        return next;
                      })
                    }
                  />
                </span>
              </div>
            ))}
          </div>
        </FluentProvider>
      </Panel>
    </>
  );
};

export default TimeServices;
